import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHero, StatHero, StatusPill } from "@/components/shared/Visual";
import {
  Activity, Users, ShieldCheck, AlertTriangle, TrendingUp,
  MapPin, Radio, ClipboardList, Clock, ExternalLink,
  Siren, Plus, Users2, PhoneCall,
} from "lucide-react";

/**
 * OWNER / CPO CONTROL ROOM
 * Wide command center view — all live deployments at a glance.
 * Available to super_admin and company.
 */
export default function ControlRoom() {
  const { user } = useAuth();
  const [data, setData] = useState({ deployments: [], summary: null });
  const [alerts, setAlerts] = useState([]);
  const [vetting, setVetting] = useState(null);
  const [emergencyOpen, setEmergencyOpen] = useState(false);

  useEffect(() => {
    const load = () => {
      api.get("/ops/deployments").then((r) => setData(r.data || {})).catch(() => {});
      api.get("/ops/alerts").then((r) => setAlerts(r.data?.alerts || [])).catch(() => {});
      api.get("/admin/cpo-vetting").then((r) => setVetting(r.data || null)).catch(() => {});
    };
    load();
    const t = setInterval(load, 25_000);
    return () => clearInterval(t);
  }, []);

  const s = data.summary || { live: 0, starting_soon: 0, upcoming: 0, personnel_on_duty: 0, personnel_expected: 0, success_rate: 0 };
  const active = (data.deployments || []).filter((d) => d.status === "live" || d.status === "starting_soon");

  return (
    <div className="space-y-6" data-testid="control-room-page">
      <PageHero
        eyebrow="Control Room"
        title={user?.role === "super_admin" ? "System Operations" : "Live Deployments"}
        sub={`${s.personnel_on_duty}/${s.personnel_expected} personnel on duty`}
        right={
          <div className="flex items-center gap-2">
            <StatusPill kind="live">System Operational</StatusPill>
          </div>
        }
      />

      {/* Command stat row — 5 KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        <StatHero label="Live Deployments" value={s.live} icon={Activity} tone="emerald" testId="cr-stat-live" />
        <StatHero label="On Duty" value={s.personnel_on_duty} icon={Users} tone="brand" testId="cr-stat-on-duty" />
        <StatHero label="Starting Soon" value={s.starting_soon} icon={Clock} tone="amber" testId="cr-stat-starting" />
        <StatHero label="Alerts" value={alerts.length} icon={AlertTriangle} tone={alerts.length ? "rose" : "brand"} testId="cr-stat-alerts" />
        <StatHero label="Success Rate" value={`${s.success_rate}%`} icon={TrendingUp} tone="cyan" testId="cr-stat-success" />
      </div>

      {/* Alerts strip */}
      {alerts.length > 0 && (
        <div className="card-dark p-4" data-testid="cr-alerts-panel">
          <div className="flex items-center justify-between mb-3">
            <div className="eyebrow eyebrow-brand flex items-center gap-2">
              <AlertTriangle className="w-3.5 h-3.5 text-rose-400" /> Active Alerts
            </div>
            <StatusPill kind="emergency">{alerts.length}</StatusPill>
          </div>
          <div className="grid gap-2 md:grid-cols-2">
            {alerts.slice(0, 6).map((a) => (
              <Link
                to={`/ops-room/${a.booking_id}`}
                key={a.id}
                className={`alert-card ${a.severity === "critical" ? "" : "warn"} hover:bg-white/5`}
                data-testid={`cr-alert-${a.id}`}
              >
                <AlertTriangle className={`w-4 h-4 flex-shrink-0 mt-0.5 ${a.severity === "critical" ? "text-rose-400" : "text-amber-400"}`} />
                <div className="flex-1 min-w-0">
                  <div className="font-outfit font-semibold txt-hi text-sm">{a.title}</div>
                  <div className="eyebrow mt-1 truncate">{a.sub}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Live deployments table */}
      <div className="card-dark p-5" data-testid="cr-deployments">
        <div className="flex items-center justify-between mb-4">
          <div className="eyebrow eyebrow-brand">Live Deployments Overview</div>
          <div className="txt-lo text-xs font-manrope">{active.length} active</div>
        </div>
        {active.length === 0 ? (
          <div className="text-center py-10 txt-lo font-manrope text-sm">No live deployments right now.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="dgrid min-w-full">
              <thead>
                <tr>
                  <th>Deployment</th>
                  <th>Location</th>
                  <th>Time</th>
                  <th className="text-center">Team</th>
                  <th>Status</th>
                  <th className="w-56">Progress</th>
                </tr>
              </thead>
              <tbody>
                {active.map((d) => {
                  const pct = d.progress_pct || 0;
                  const cls = pct >= 90 ? "pbar-ok" : pct >= 60 ? "" : pct > 0 ? "pbar-warn" : "pbar-err";
                  return (
                    <tr key={d.id} data-testid={`cr-deploy-${d.id}`}>
                      <td>
                        <div className="font-outfit font-semibold txt-hi">{d.title}</div>
                        <div className="text-xs txt-lo font-manrope">{d.company_name}</div>
                      </td>
                      <td>{d.location}</td>
                      <td>{d.start_time || "—"} – {d.end_time || "—"}</td>
                      <td className="text-center"><span className="font-outfit font-bold txt-hi">{d.checked_in}/{d.workers}</span></td>
                      <td>
                        <StatusPill kind={d.status === "live" ? "live" : d.status === "starting_soon" ? "standby" : "tracked"}>
                          {d.status === "live" ? "Live" : d.status === "starting_soon" ? "Soon" : "Upcoming"}
                        </StatusPill>
                      </td>
                      <td>
                        <div className="flex items-center gap-2">
                          <div className={`pbar ${cls}`}><span style={{ width: `${pct}%` }} /></div>
                          <span className="text-xs txt-md font-mono w-10 text-right">{pct}%</span>
                          {d.ops_room && (
                            <Link to={`/ops-room/${d.id}`} className="txt-lo hover:text-brand"><ExternalLink className="w-3.5 h-3.5" /></Link>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Vetting Status + Emergency Room + Quick Actions */}
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="card-dark p-5" data-testid="cr-vetting-status">
          <div className="flex items-center justify-between mb-4">
            <div className="eyebrow eyebrow-brand flex items-center gap-2"><ShieldCheck className="w-3.5 h-3.5" /> Vetting Status</div>
            {user?.role === "super_admin" && (
              <Link to="/admin/staff" className="text-xs text-brand font-outfit font-semibold hover:underline">Manage</Link>
            )}
          </div>
          {vetting ? (
            <div className="space-y-3">
              <VetRow tone="amber"   n={vetting.provisional}    label="Provisionally Vetted" sub="Amber — initial vetting" />
              <VetRow tone="brand"   n={vetting.on_jobs}         label="In Assessment" sub="Currently on live jobs" />
              <VetRow tone="emerald" n={vetting.verified_pool}   label="Fully Verified" sub="Green — vetted and approved" />
              <VetRow tone="cyan"    n={vetting.in_queue}        label="Applications in Queue" sub="Awaiting review" />
            </div>
          ) : <div className="txt-lo text-sm font-manrope">Loading…</div>}
        </div>

        <div className="card-dark p-5 relative overflow-hidden" data-testid="cr-emergency-room">
          <div className="absolute inset-0" style={{ background: "radial-gradient(360px 200px at 100% 0%, rgba(239,68,68,0.18), transparent 60%)" }} />
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-3">
              <div className="eyebrow flex items-center gap-2 text-rose-300"><Siren className="w-3.5 h-3.5" /> Emergency Room</div>
              <StatusPill kind={emergencyOpen ? "emergency" : "neutral"}>{emergencyOpen ? "Active" : "Standby"}</StatusPill>
            </div>
            <div className="text-sm txt-md font-manrope">Broadcast to team leads + on-call CPOs. Opens a live operations room.</div>
            <button
              onClick={() => setEmergencyOpen((v) => !v)}
              className="mt-4 w-full py-3 rounded-lg bg-rose-500 hover:bg-rose-600 text-white font-outfit font-bold flex items-center justify-center gap-2"
              data-testid="cr-launch-emergency"
            >
              <Siren className="w-4 h-4" /> {emergencyOpen ? "Close Emergency Room" : "Launch Emergency Room"}
            </button>
          </div>
        </div>

        <div className="card-dark p-5" data-testid="cr-quick-actions">
          <div className="eyebrow eyebrow-brand mb-4">Quick Actions</div>
          <div className="space-y-2">
            <QuickAction to="/company/jobs" icon={Plus} label="Post a Job" tone="brand" />
            <QuickAction to="/company/teams" icon={Users2} label="Build a Team" tone="violet" />
            <QuickAction onClick={() => setEmergencyOpen(true)} icon={PhoneCall} label="Emergency Call" tone="rose" />
          </div>
        </div>
      </div>

      {/* Bottom grid: map placeholder + recent activity */}
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="card-dark p-5 lg:col-span-2" data-testid="cr-live-map">
          <div className="flex items-center justify-between mb-3">
            <div className="eyebrow eyebrow-brand flex items-center gap-2"><MapPin className="w-3.5 h-3.5" /> Live Map</div>
            <StatusPill kind="live">{active.length} pins</StatusPill>
          </div>
          <div className="relative h-64 rounded-lg overflow-hidden card-inner flex items-center justify-center">
            <div
              className="absolute inset-0 opacity-30"
              style={{
                background:
                  "radial-gradient(600px 300px at 30% 40%, rgba(0,102,255,0.35), transparent 60%), radial-gradient(400px 200px at 70% 60%, rgba(34,211,238,0.25), transparent 60%)",
              }}
            />
            <div className="relative z-10 text-center">
              <MapPin className="w-8 h-8 text-brand mx-auto mb-2" />
              <div className="txt-md font-manrope text-sm">Map view (coming soon)</div>
              <div className="eyebrow mt-1">{active.length} deployments tracked</div>
            </div>
            {/* Pin markers */}
            {active.slice(0, 5).map((d, i) => (
              <div
                key={d.id}
                className="absolute w-3 h-3 rounded-full bg-gold-400 shadow-[0_0_16px_4px_rgba(34,197,94,0.5)]"
                style={{ left: `${15 + i * 18}%`, top: `${30 + (i % 3) * 20}%` }}
                title={d.title}
              />
            ))}
          </div>
        </div>

        <div className="card-dark p-5" data-testid="cr-system-status">
          <div className="eyebrow eyebrow-brand mb-3 flex items-center gap-2"><Radio className="w-3.5 h-3.5" /> System Status</div>
          <div className="space-y-3">
            {[
              ["Check-in / GPS", true],
              ["Ops Rooms", true],
              ["Briefing Rooms", true],
              ["Compliance PDF", true],
            ].map(([label, ok]) => (
              <div key={label} className="flex items-center justify-between">
                <span className="text-sm txt-md font-manrope">{label}</span>
                <StatusPill kind={ok ? "active" : "emergency"}>{ok ? "Online" : "Down"}</StatusPill>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function VetRow({ n, label, sub, tone }) {
  const bg = {
    amber: "bg-amber-500/15 text-amber-300 border-amber-500/40",
    brand: "bg-brand/15 text-brand border-brand/30",
    emerald: "bg-gold-500/15 text-gold-300 border-gold-500/40",
    cyan: "bg-cyan-500/15 text-cyan-300 border-cyan-500/40",
  }[tone] || "bg-brand/15 text-brand border-brand/30";
  return (
    <div className="flex items-center gap-3">
      <div className={`w-11 h-11 rounded-md border ${bg} flex items-center justify-center font-outfit font-bold text-lg`}>{n}</div>
      <div className="min-w-0">
        <div className="font-outfit font-semibold txt-hi text-sm">{label}</div>
        <div className="eyebrow mt-0.5 truncate">{sub}</div>
      </div>
    </div>
  );
}

function QuickAction({ to, onClick, icon: Icon, label, tone }) {
  const bg = {
    brand: "bg-brand hover:bg-brand-hover text-white",
    violet: "bg-violet-500 hover:bg-violet-600 text-white",
    rose: "bg-rose-500 hover:bg-rose-600 text-white",
  }[tone];
  const inner = (
    <div className={`w-full py-2.5 px-3 rounded-md ${bg} flex items-center gap-2 font-outfit font-semibold text-sm`}>
      <Icon className="w-4 h-4" /> {label}
    </div>
  );
  return to ? <Link to={to} className="block">{inner}</Link> : <button onClick={onClick} className="w-full">{inner}</button>;
}
